/**
 * Amazon 商品管理（Product Content Manager + AI Copilot）の契約型。
 * 正本: tmp/fable/2026-10-04-amazon-product-manager/plan.md §3。
 * stoq-worker/src/amazon/content-types.ts と**同一内容**を保つ（片方を変えたらもう片方も変える）。
 *
 * 属性は SP-API Listings Items の attributes 形（{ attr: [ {value, language_tag, marketplace_id, ...} ] }）を
 * そのまま保持する。current（Amazon現在値）と draft（ローカル下書き）は同形。差分は属性キー単位。
 */

export type AmzAttributes = Record<string, unknown[]>;

export type AmzFieldType =
  | "text"
  | "textarea"
  | "number"
  | "boolean"
  | "enum"
  | "measure"
  | "dimensions"
  | "json";

export interface AmzOption {
  value: string;
  label: string;
}

/** Product Type Definitions の JSON Schema から worker が生成する、画面用の軽量フィールド定義。 */
export interface AmzFieldDef {
  /** 属性キー（item_name 等） */
  key: string;
  /** schema title（日本語） */
  label: string;
  description?: string;
  /** propertyGroups のキー（product_identity / product_details / images / ...） */
  group: string;
  groupTitle: string;
  type: AmzFieldType;
  required: boolean;
  /** false = 画面から変更不可（LOCKED_ATTRS または schema editable:false） */
  editable: boolean;
  /** true = AI Copilot の提案対象（自由文フィールドのみ） */
  aiAllowed: boolean;
  /** true = 画面に出さない（在庫・価格・オファー系） */
  hidden: boolean;
  maxLength?: number;
  maxBytes?: number;
  /** Amazon.co.jp の推奨上限（表示用。超過は warning・schema の maxLength 超過は error） */
  softMax?: number;
  /** maxUniqueItems（bullet_point=10, material=14 など） */
  maxItems?: number;
  /** maxItems > 1 のとき true（値は string[]） */
  multi: boolean;
  options?: AmzOption[];
  /** anyOf [string | enum]: 選択肢に無い自由入力も可 */
  freeEnum?: boolean;
  /** measure / dimensions の単位候補 */
  units?: AmzOption[];
  hasLanguageTag: boolean;
  /** 'AI編集なし' | '編集不可' */
  lockNote?: string;
}

export interface AmzProductTypeFields {
  productType: string;
  displayName: string;
  fetchedAt: string;
  /** worker 側の定数（softMax・AI可否など）を変えたときにキャッシュを無効化するための版。AMZ_FIELD_MODEL_VERSION と一致しなければ再生成 */
  modelVersion?: number;
  required: string[];
  groups: { key: string; title: string }[];
  fields: AmzFieldDef[];
}

/** 画面・AI・履歴で共通のフィールド識別子。属性キー / `bullet_point[0..9]` / `images`。 */
export type AmzFieldKey = string;

export type AmzSource = "AI" | "MANUAL";
export type AmzHistorySource = "AI" | "MANUAL" | "AMAZON_SYNC" | "SYSTEM";
export type AmzSeverity = "ERROR" | "WARNING" | "INFO";

export interface AmzIssue {
  code: string;
  message: string;
  severity: AmzSeverity;
  attributeNames: string[];
  categories?: string[];
}

export interface AmzProductSummary {
  asin: string | null;
  itemName: string | null;
  status: string[];
  mainImage: string | null;
  /** purchasable_offer の value_with_tax（表示のみ・編集不可） */
  price: number | null;
  /** fulfillmentAvailability のチャネル（DEFAULT / AMAZON_JP ...）表示のみ */
  channels: string[];
}

export interface AmzProductRow {
  sku: string;
  asin: string | null;
  productType: string | null;
  itemName: string | null;
  /** true = Amazon に出品が存在する（amazon_listings キャッシュに行がある） */
  registered: boolean;
  status: string[];
  /** current と draft の差分件数（未登録SKUは draft の属性数） */
  draftChanges: number;
  issues: { error: number; warning: number; info: number };
  lastSyncedAt: string | null;
  syncError: string | null;
  mainImage: string | null;
  price: number | null;
  /** 下書き最終更新 */
  draftUpdatedAt: string | null;
}

export interface AmzAttention {
  /** 'ERROR' | 'API Sync Failed' | '未登録' | 'Amazonとの差分' */
  level: string;
  tone: "error" | "warning" | "accent" | "info" | "neutral" | "success";
  text: string;
  sku: string;
  itemName: string | null;
  /** 遷移先タブ */
  tab: "overview" | "page" | "images" | "issues" | "history";
}

export interface AmzHistoryRow {
  id: number;
  createdAt: string;
  sku: string;
  user: string;
  source: AmzHistorySource;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  result: "成功" | "失敗";
  submissionId: string | null;
}

export interface AmzOverviewResponse {
  ok: true;
  generatedAt: string;
  marketplaceId: string;
  sellerId: string;
  /** env AMAZON_LISTING_WRITE_LIVE === '1' */
  writeLive: boolean;
  lastListingsSyncAt: string | null;
  stats: {
    products: number;
    review: number;
    errors: number;
    unregistered: number;
    draftChanged: number;
    syncErrors: number;
  };
  attention: AmzAttention[];
  recent: AmzHistoryRow[];
  products: AmzProductRow[];
}

export interface AmzProductDetail {
  sku: string;
  asin: string | null;
  productType: string;
  requirements: string;
  registered: boolean;
  summary: AmzProductSummary;
  /** Amazon 現在値。未登録SKUは null */
  current: AmzAttributes | null;
  draft: AmzAttributes;
  sources: Record<AmzFieldKey, AmzSource>;
  issues: AmzIssue[];
  currentSyncedAt: string | null;
  syncError: string | null;
  draftUpdatedAt: string;
  fields: AmzProductTypeFields;
  marketplaceId: string;
  writeLive: boolean;
}

export interface AmzProductDetailResponse {
  ok: true;
  detail: AmzProductDetail;
}

export interface AmzProductSyncResponse {
  ok: true;
  detail: AmzProductDetail;
  /** 下書きで編集済みのため Amazon の新しい値で上書きしなかったフィールド */
  conflicts: AmzFieldKey[];
}

export interface AmzSaveDraftInput {
  draft: AmzAttributes;
  sources: Record<AmzFieldKey, AmzSource>;
}

export interface AmzChange {
  key: AmzFieldKey;
  label: string;
  kind: "text" | "attribute" | "image";
  oldValue: string;
  newValue: string;
  source: AmzSource | null;
}

export interface AmzSaveDraftResponse {
  ok: true;
  draftUpdatedAt: string;
  changes: AmzChange[];
}

export interface AmzImportInput {
  sku: string;
  productType: string;
  requirements?: string;
  attributes: AmzAttributes;
}

export type AmzValidationStatus = "ok" | "warn" | "error" | "skip";

export interface AmzValidation {
  name: "Schema Validation" | "Business Validation" | "Compliance Validation" | "Amazon Validation";
  status: AmzValidationStatus;
  detail: string;
  items?: string[];
}

export interface AmzPreviewResponse {
  ok: true;
  changes: AmzChange[];
  changesHash: string;
  validations: AmzValidation[];
  amazonIssues: AmzIssue[];
  operation: "PATCH" | "PUT";
  requestJson: unknown;
  canPublish: boolean;
  writeLive: boolean;
}

export interface AmzPublishInput {
  writeConfirmed: true;
  changesHash: string;
}

export interface AmzPublishResponse {
  ok: true;
  result: { status: string; submissionId: string | null; issues: AmzIssue[] };
  developer: {
    requestId: string | null;
    api: string;
    operation: "patchListingsItem" | "putListingsItem";
    endpoint: string;
    marketplace: string;
    statusCode: number;
    requestJson: unknown;
  };
  historyAdded: number;
  detail: AmzProductDetail;
}

export interface AmzAiFinding {
  level: AmzSeverity;
  item: string;
  text: string;
}

export interface AmzAiProposal {
  field: AmzFieldKey;
  label: string;
  oldValue: string;
  newValue: string;
  reason: string;
  risk: "低" | "中" | "高";
  /** 0..100 */
  confidence: number;
}

export interface AmzAiInput {
  target: AmzFieldKey | null;
  instruction: string;
  draft: AmzAttributes;
}

export interface AmzAiResponse {
  ok: true;
  /** 'gemini' | 'claude' | 'rules'（LLM失敗時はルール検査のみ） */
  model: string;
  message: string;
  findings: AmzAiFinding[];
  proposals: AmzAiProposal[];
  /** brand-reviewer 相当の確認行（固定文） */
  brandNote: string;
}

export interface AmzHistoryResponse {
  ok: true;
  rows: AmzHistoryRow[];
}

/** 画像スロット。main_product_image_locator + other_product_image_locator_1..8 の順。 */
export const AMZ_IMAGE_ATTRS = [
  "main_product_image_locator",
  "other_product_image_locator_1",
  "other_product_image_locator_2",
  "other_product_image_locator_3",
  "other_product_image_locator_4",
  "other_product_image_locator_5",
  "other_product_image_locator_6",
  "other_product_image_locator_7",
  "other_product_image_locator_8",
] as const;

/** 画面に出さない属性（在庫・価格・オファー系。前方一致で判定する接頭辞も含む）。 */
export const AMZ_HIDDEN_ATTRS = [
  "fulfillment_availability",
  "purchasable_offer",
  "skip_offer",
  "merchant_shipping_group",
  "max_order_quantity",
  "main_offer_image_locator",
  "optional_payment_type_exclusion",
] as const;
export const AMZ_HIDDEN_PREFIXES = ["other_offer_image_locator_", "automated_pricing_"] as const;

/** 画面から変更不可（表示はする）。 */
export const AMZ_LOCKED_ATTRS = [
  "externally_assigned_product_identifier",
  "merchant_suggested_asin",
  "parentage_level",
  "child_parent_sku_relationship",
  "variation_theme",
  "condition_type",
  "list_price",
] as const;

/** AI Copilot の提案対象（自由文）。これ以外は aiAllowed=false。 */
export const AMZ_AI_ALLOWED_ATTRS = [
  "item_name",
  "title_differentiation",
  "bullet_point",
  "product_description",
  "generic_keyword",
  "special_feature",
  "style",
  "color",
  "size",
  "included_components",
  "item_shape",
  "model_name",
] as const;

/** Amazon.co.jp 推奨上限（表示用の softMax）。 */
/** フィールド定義モデルの版（softMax・AI可否・固定属性を変えたら +1 → D1 の amazon_pt_fields キャッシュが自動で作り直される） */
export const AMZ_FIELD_MODEL_VERSION = 2;

export const AMZ_SOFT_MAX: Record<string, number> = {
  item_name: 75,
  title_differentiation: 125,
  bullet_point: 100,
  product_description: 600,
  generic_keyword: 250,
};
